import React, { useEffect, useState } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  UserIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  ShieldCheckIcon,
  ArchiveBoxIcon,
  ArrowUturnLeftIcon
} from '@heroicons/react/24/outline';
import { UsersClient, ApplicationUserDto } from '../../web-api-client';
import { createApiClient } from '../../utils/apiClientFactory';
import UserModal from './UserModal';
import { Roles } from '../../constants/roles';

export default function SystemUsers() {
  const [users, setUsers] = useState<ApplicationUserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ApplicationUserDto | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const fetchUsers = async () => {
    try {
      const client = createApiClient(UsersClient);
      const data = await client.getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const archivedCount = users.filter(user => user.isArchived).length;

  const filteredUsers = users.filter(user =>
    (showArchived ? user.isArchived : !user.isArchived) &&
    (user.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.roles?.some((r: string) => r.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  const handleAddUser = () => {
    setSelectedUser(null);
    setShowModal(true);
  };

  const handleEditUser = (user: ApplicationUserDto) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleToggleStatus = async (userId: string, isActive: boolean) => {
    try {
      setActionLoading(userId);
      const client = createApiClient(UsersClient);

      if (isActive) {
        await client.deactivateUser(userId);
      } else {
        await client.activateUser(userId);
      }

      await fetchUsers();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchive = async (userId: string) => {
    try {
      setActionLoading(userId);
      const client = createApiClient(UsersClient);
      await client.archiveUser(userId);
      await fetchUsers();
    } catch (error) {
      console.error('Failed to archive user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnarchive = async (userId: string) => {
    try {
      setActionLoading(userId);
      const client = createApiClient(UsersClient);
      await client.unarchiveUser(userId);
      await fetchUsers();
    } catch (error) {
      console.error('Failed to restore user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleModalClose = async (refresh: boolean) => {
    setShowModal(false);
    setSelectedUser(null);
    if (refresh) {
      await fetchUsers();
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case Roles.Administrator:
        return 'bg-red-100 text-red-800 border-red-200';
      case Roles.SystemOwner:
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case Roles.HRMaster:
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case Roles.ITSupport:
        return 'bg-green-100 text-green-800 border-green-200';
      case Roles.DepartmentAdmin:
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case Roles.Superior:
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case Roles.Approver:
        return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case Roles.PIC:
        return 'bg-teal-100 text-teal-800 border-teal-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:border-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="mb-6">
          <div className="h-8 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-96"></div>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="p-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4 py-4">
                <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg">
                <ShieldCheckIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">System Users</h1>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Manage system users and their roles
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={handleAddUser}
            className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg hover:shadow-xl text-white font-medium transition-all duration-200 hover:scale-105"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add User
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="relative flex-1 max-w-2xl">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by username, email, name, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-12 pr-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl leading-5 bg-white dark:bg-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`inline-flex items-center px-4 py-3 rounded-xl border-2 font-medium text-sm transition-all duration-200 ${
              showArchived
                ? 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <ArchiveBoxIcon className="h-5 w-5 mr-2" />
            {showArchived ? 'Viewing Archived' : 'Show Archived'}
            {archivedCount > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                {archivedCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Users table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Roles
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      {showArchived ? 'No archived users' : 'No users found'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:bg-gray-700 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold">
                            {user.fullName?.charAt(0) || user.userName?.charAt(0) || 'U'}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.fullName || user.userName}
                          </div>
                          {user.fullName && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">@{user.userName}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{user.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        {user.roles?.map((role: string, idx: number) => (
                          <span
                            key={idx}
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(role)}`}
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.isArchived ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                          <ArchiveBoxIcon className="h-4 w-4 mr-1" />
                          Archived
                        </span>
                      ) : user.isActive ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(user.createdAt + 'Z').toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        {user.isArchived ? (
                          <button
                            onClick={() => handleUnarchive(user.id!)}
                            disabled={actionLoading === user.id}
                            className="inline-flex items-center px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {actionLoading === user.id ? (
                              <span className="inline-block h-4 w-4 mr-1 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <ArrowUturnLeftIcon className="h-4 w-4 mr-1" />
                            )}
                            Restore
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEditUser(user)}
                              className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors duration-150"
                            >
                              <PencilIcon className="h-4 w-4 mr-1" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleStatus(user.id!, user.isActive!)}
                              disabled={actionLoading === user.id}
                              className={`inline-flex items-center px-3 py-1.5 rounded-lg transition-colors duration-150 ${
                                user.isActive
                                  ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                  : 'bg-green-50 text-green-700 hover:bg-green-100'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {actionLoading === user.id ? (
                                <span className="inline-block h-4 w-4 mr-1 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              ) : user.isActive ? (
                                <XCircleIcon className="h-4 w-4 mr-1" />
                              ) : (
                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                              )}
                              {user.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            {!user.isActive && (
                              <button
                                onClick={() => handleArchive(user.id!)}
                                disabled={actionLoading === user.id}
                                className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Archive this user to hide it from the list"
                              >
                                <ArchiveBoxIcon className="h-4 w-4 mr-1" />
                                Archive
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <UserModal
          user={selectedUser}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}
